import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/common';
import { DEFAULT_OU_ENABLED, DEFAULT_CONFIG } from './constants';
import { RuleOUSection, RuleTimeOUSection, Rn002OUSection } from './RuleOUSections';
import { useRules, useUpdateRule, useUpsertRuleOuOverride } from '@/hooks/useRules';
import { useOUItems } from '@/hooks/useOUs';
import type { OUItem } from '@/api/types';
import { useOuExpansion, collectDescendantItems, OuTreeCard } from '@/components/common/OuTree';

export function RulesTab() {
  const { t } = useTranslation();

  // RN-000: OU app toggles — hydrated from rules_engine.business_rules (OU_ACTIVE code + per-ouId overrides)
  const [ouEnabled, setOuEnabled] = useState<Record<string, boolean>>(DEFAULT_OU_ENABLED);

  // RN-001
  const [rn001Enabled, setRn001Enabled] = useState(DEFAULT_CONFIG.rn001Enabled);
  const [rn001, setRn001] = useState(DEFAULT_CONFIG.rn001);
  const [rn001OU, setRn001OU] = useState(false);
  const [rn001Modal, setRn001Modal] = useState(false);

  // RN-002
  const [rn002Enabled, setRn002Enabled] = useState(true);
  const [exclRecur, setExclRecur] = useState(true);
  const [exclWork, setExclWork] = useState(true);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [kwInput, setKwInput] = useState('');
  const [rn002OU, setRn002OU] = useState(false);
  const [rn002Modal, setRn002Modal] = useState(false);

  // RN-003
  const [rn003Enabled, setRn003Enabled] = useState(DEFAULT_CONFIG.rn003Enabled);
  const [rn003, setRn003] = useState(DEFAULT_CONFIG.rn003);
  const [rn003OU, setRn003OU] = useState(false);
  const [rn003Modal, setRn003Modal] = useState(false);

  // RN-004
  const [rn004Enabled, setRn004Enabled] = useState(DEFAULT_CONFIG.rn004Enabled);
  const [rn004, setRn004] = useState(DEFAULT_CONFIG.rn004);
  const [rn004OU, setRn004OU] = useState(false);
  const [rn004Modal, setRn004Modal] = useState(false);

  // RN-005
  const [rn005Enabled, setRn005Enabled] = useState(DEFAULT_CONFIG.rn005Enabled);
  const [rn005, setRn005] = useState(DEFAULT_CONFIG.rn005);
  const [rn005OU, setRn005OU] = useState(false);
  const [rn005Modal, setRn005Modal] = useState(false);

  // Save status feedback
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const updateRule = useUpdateRule();
  const upsertOuOverride = useUpsertRuleOuOverride();
  const { data: rulesConfig } = useRules();
  const { data: ouItems = [], isLoading: ousLoading } = useOUItems();

  const { tree: ouTree, isCollapsed: isOuTreeNodeCollapsed, toggle: toggleOuTreeNode } = useOuExpansion(ouItems);

  // IDs de OUs cuyo switch debe mostrarse apagado y bloqueado porque algún ancestro
  // (en cualquier nivel) está actualmente apagado. Se recalcula cada vez que cambia
  // qué OUs están encendidas/apagadas.
  const blockedByInactiveAncestor = useMemo(() => {
    const blockedIds = new Set<string>();
    ouItems.forEach(candidateOu => {
      if (ouEnabled[candidateOu.id] === false) {
        collectDescendantItems(ouItems, candidateOu.id).forEach(descendantOu => blockedIds.add(descendantOu.id));
      }
    });
    return blockedIds;
  }, [ouItems, ouEnabled]);

  /**
   * Cambia el estado de una OU. Al apagar, cascada real hacia todos los descendientes
   * (quedan apagados en el estado, no solo bloqueados visualmente). Al encender, solo
   * se enciende la OU indicada — los descendientes que quedaron apagados por una cascada
   * anterior se reactivan manualmente, uno por uno (igual que la cascada real del backend
   * para la pantalla de OUs: SetOuActiveCommandHandler.DeactivateDescendantsAsync).
   */
  function handleOuToggle(ouId: string, nextEnabled: boolean) {
    if (nextEnabled) {
      setOuEnabled(previousState => ({ ...previousState, [ouId]: true }));
      return;
    }
    const descendantIds = collectDescendantItems(ouItems, ouId).map(descendantOu => descendantOu.id);
    setOuEnabled(previousState => {
      const nextState = { ...previousState, [ouId]: false };
      descendantIds.forEach(descendantId => { nextState[descendantId] = false; });
      return nextState;
    });
  }

  function renderOuToggleContent(ouItem: OUItem) {
    const isBlocked = blockedByInactiveAncestor.has(ouItem.id);
    const isEnabled = ouEnabled[ouItem.id] ?? true;
    return (
      <>
        {isEnabled ? (
          <span className="rule-ou-toggle-status rule-ou-toggle-status--on">
            <i className="fas fa-check-circle" /> {t('admin.ouActive')}
          </span>
        ) : (
          <span className="rule-ou-toggle-status rule-ou-toggle-status--off">
            <i className="fas fa-times-circle" /> {t('admin.ouDisabled')}
          </span>
        )}
        <label className={`switch${isBlocked ? ' switch--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={isEnabled}
            disabled={isBlocked}
            onChange={toggleEvent => handleOuToggle(ouItem.id, toggleEvent.target.checked)}
          />
          <span className="slider" />
        </label>
      </>
    );
  }

  const RN001_FIELD_CODES: Record<string, string> = {
    minDur: 'MEETING_MIN_DURATION_MINUTES',
    maxDur: 'MEETING_MAX_DURATION_MINUTES',
    minPax: 'MEETING_MIN_PARTICIPANTS',
    maxPax: 'MEETING_MAX_PARTICIPANTS',
  };
  const RN004_FIELD_CODES: Record<string, string> = {
    maxWeek:  'MAX_EVALUATIONS_PER_WEEK',
    maxPer:   'T5_EVALUATION_COUNT',
    interval: 'SURVEY_MIN_INTERVAL_HOURS',
  };
  const RN005_FIELD_CODES: Record<string, string> = {
    maxDay: 'MAX_EVALUATIONS_PER_DAY',
  };

  async function handleOuSave(
    fieldCodes: Record<string, string>,
    ouId: string,
    values: Record<string, number> | null,
  ) {
    await Promise.all(
      Object.entries(fieldCodes).map(([fieldKey, code]) =>
        upsertOuOverride.mutateAsync({ code, ouId, value: values ? String(values[fieldKey]) : null })
      )
    );
  }

  async function handleRn002OuSave(
    ouId: string,
    state: { recurr: boolean; work: boolean; keywords: string[] } | null,
  ) {
    await Promise.all([
      upsertOuOverride.mutateAsync({ code: 'EXCLUDE_RECURRING_MEETINGS', ouId, value: state ? String(state.recurr) : null }),
      upsertOuOverride.mutateAsync({ code: 'EXCLUDE_OUTSIDE_WORK_HOURS', ouId, value: state ? String(state.work)   : null }),
      upsertOuOverride.mutateAsync({ code: 'EXCLUDE_KEYWORDS',           ouId, value: state ? state.keywords.join(',')  : null }),
    ]);
  }

  // Hydrate all rule states from backend (rules_engine.business_rules)
  useEffect(() => {
    if (!rulesConfig) return;
    // RN-000: OU active toggles
    if (rulesConfig.ouActive) setOuEnabled({ ...rulesConfig.ouActive });
    setRn001Enabled(rulesConfig.rn001.enabled);
    setRn001({ minDur: rulesConfig.rn001.minDur, maxDur: rulesConfig.rn001.maxDur, minPax: rulesConfig.rn001.minPax, maxPax: rulesConfig.rn001.maxPax });
    setRn002Enabled(rulesConfig.rn002.enabled);
    setExclRecur(rulesConfig.rn002.exclRecur);
    setExclWork(rulesConfig.rn002.exclWork);
    setKeywords(rulesConfig.rn002.keywords);
    setRn003Enabled(rulesConfig.rn003.enabled);
    setRn003({ from: rulesConfig.rn003.from, to: rulesConfig.rn003.to });
    setRn004Enabled(rulesConfig.rn004.enabled);
    setRn004({ maxWeek: rulesConfig.rn004.maxWeek, maxPer: rulesConfig.rn004.maxPer, interval: rulesConfig.rn004.interval });
    setRn005Enabled(rulesConfig.rn005.enabled);
    setRn005({ maxDay: rulesConfig.rn005.maxDay });
  }, [rulesConfig]);
  // Confirm modal for "Restaurar defaults"
  const [confirmRestore, setConfirmRestore] = useState(false);

  /** Reset all rule states to hardcoded defaults. */
  function resetToDefaults() {
    setOuEnabled(Object.fromEntries(ouItems.map(item => [item.id, item.isActive ?? true])));
    setRn001Enabled(DEFAULT_CONFIG.rn001Enabled);
    setRn001({ ...DEFAULT_CONFIG.rn001 });
    setRn002Enabled(DEFAULT_CONFIG.rn002Enabled);
    setExclRecur(DEFAULT_CONFIG.rn002.exclRecur);
    setExclWork(DEFAULT_CONFIG.rn002.exclWork);
    setKeywords([]);
    setKwInput('');
    setRn003Enabled(DEFAULT_CONFIG.rn003Enabled);
    setRn003({ ...DEFAULT_CONFIG.rn003 });
    setRn004Enabled(DEFAULT_CONFIG.rn004Enabled);
    setRn004({ ...DEFAULT_CONFIG.rn004 });
    setRn005Enabled(DEFAULT_CONFIG.rn005Enabled);
    setRn005({ ...DEFAULT_CONFIG.rn005 });
    // Close any open OU panels
    setRn001OU(false); setRn001Modal(false);
    setRn002OU(false); setRn002Modal(false);
    setRn003OU(false); setRn003Modal(false);
    setRn004OU(false); setRn004Modal(false);
    setRn005OU(false); setRn005Modal(false);
  }

  async function handleSave() {
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');

    try {
      // RN-001 to RN-005: save rule values + isActive to RulesEngine
      const rules = [
        { code: 'MEETING_MIN_DURATION_MINUTES', value: String(rn001.minDur),  isActive: rn001Enabled, description: 'Minimum meeting duration (minutes)' },
        { code: 'MEETING_MAX_DURATION_MINUTES', value: String(rn001.maxDur),  isActive: rn001Enabled, description: 'Maximum meeting duration (minutes)' },
        { code: 'MEETING_MIN_PARTICIPANTS',     value: String(rn001.minPax),  isActive: rn001Enabled, description: 'Minimum meeting participants' },
        { code: 'MEETING_MAX_PARTICIPANTS',     value: String(rn001.maxPax),  isActive: rn001Enabled, description: 'Maximum meeting participants' },
        { code: 'EXCLUDE_RECURRING_MEETINGS',   value: String(exclRecur),          isActive: rn002Enabled, description: 'Exclude recurring meetings' },
        { code: 'EXCLUDE_OUTSIDE_WORK_HOURS',   value: String(exclWork),           isActive: rn002Enabled, description: 'Exclude meetings outside work hours' },
        { code: 'EXCLUDE_KEYWORDS',             value: keywords.join(','),         isActive: rn002Enabled, description: 'Comma-separated keywords to exclude' },
        { code: 'WORK_HOURS_START',             value: rn003.from,            isActive: rn003Enabled, description: 'Work hours start' },
        { code: 'WORK_HOURS_END',               value: rn003.to,              isActive: rn003Enabled, description: 'Work hours end' },
        { code: 'MAX_EVALUATIONS_PER_WEEK',     value: String(rn004.maxWeek), isActive: rn004Enabled, description: 'Maximum evaluations per week' },
        { code: 'T5_EVALUATION_COUNT',          value: String(rn004.maxPer),      isActive: rn004Enabled, description: 'T5 evaluation count' },
        { code: 'SURVEY_MIN_INTERVAL_HOURS',    value: String(rn004.interval),    isActive: rn004Enabled, description: 'Minimum interval between surveys (hours)' },
        { code: 'MAX_EVALUATIONS_PER_DAY',      value: String(rn005.maxDay),      isActive: rn005Enabled, description: 'Maximum evaluations per day' },
      ];

      await Promise.all(
        rules.map(r => updateRule.mutateAsync({ code: r.code, body: { description: r.description, value: r.value, isActive: r.isActive } }))
      );

      // RN-000: save OU active states via OU_ACTIVE canonical rule + per-OU override
      await Promise.all(
        ouItems.map(item => {
          const isActive = ouEnabled[item.id] ?? (item.isActive ?? true);
          return upsertOuOverride.mutateAsync({ code: 'OU_ACTIVE', ouId: item.id, value: String(isActive) });
        })
      );

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }

  function addKeyword() {
    const kw = kwInput.trim();
    if (!kw || keywords.includes(kw)) return;
    setKeywords(ks => [...ks, kw]);
    setKwInput('');
  }

  function removeKeyword(kw: string) {
    setKeywords(ks => ks.filter(k => k !== kw));
  }

  return (
    <div className="adm-tab-content">
      {confirmRestore && (
        <ConfirmModal
          type="warning"
          title={t('admin.restoreDefaultsTitle')}
          message={t('admin.restoreDefaultsDesc')}
          confirmText={t('admin.restoreDefaultsConfirm')}
          onConfirm={() => { resetToDefaults(); setConfirmRestore(false); }}
          onClose={() => setConfirmRestore(false)}
        />
      )}

      <header className="page-header page-header--center">
        <h2 className="page-title">{t('admin.rulesTitle')}</h2>
        <button className="btn btn--ghost btn--sm" onClick={() => setConfirmRestore(true)}>
          <i className="fas fa-undo" /> {t('admin.restoreDefaults')}
        </button>
      </header>

      <div className="rules-grid">

        {/* RN-000: Desactivar App por OU — full width */}
        <div className="rules-card full-width">
          <div className="rules-card-header">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0 }}>
                  <i className="fas fa-power-off" style={{ color: 'var(--cp-red)' }} /> {t('admin.rn000Title')}
                </h3>
              </div>
              <span className="rules-subtitle">{t('admin.rn000Subtitle')}</span>
            </div>
          </div>
          <div className="rules-card-body">
            {ousLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0', color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                {t('common.loading')}
              </div>
            ) : (
            <div className="ona2-tree">
              {ouTree.map(node => (
                <OuTreeCard
                  key={node.ou.id}
                  node={node}
                  depth={0}
                  isCollapsed={isOuTreeNodeCollapsed}
                  onToggle={toggleOuTreeNode}
                  renderContent={renderOuToggleContent}
                />
              ))}
            </div>
            )}
            <div className="anonymity-description" style={{ marginTop: 12 }}>
              <i className="fas fa-exclamation-triangle" style={{ color: 'var(--cp-danger)' }} />
              <p>{t('admin.rn000Warning')}</p>
            </div>
          </div>
        </div>

        {/* RN-001: Criterios de Reunión */}
        <div className={`rules-card${!rn001Enabled ? ' rules-card-disabled' : ''}`}>
          <div className="rules-card-header">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0 }}><i className="fas fa-video" /> {t('admin.rn001Title')}</h3>
                <label className="switch" style={{ marginLeft: 'auto' }}>
                  <input type="checkbox" checked={rn001Enabled} onChange={e => setRn001Enabled(e.target.checked)} />
                  <span className="slider" />
                </label>
              </div>
              <span className="rules-subtitle">{t('admin.rn001Subtitle')}</span>
            </div>
          </div>
          <div className="rules-card-body">
            {([
              { label: t('admin.fieldMinSessionDuration'), key: 'minDur', min: 5,  max: 60,  suffix: t('admin.suffixMinutes') },
              { label: t('admin.fieldMaxSessionDuration'), key: 'maxDur', min: 30, max: 480, suffix: t('admin.suffixMinutes') },
              { label: t('admin.fieldMinParticipants'),    key: 'minPax', min: 2,  max: 50,  suffix: t('admin.suffixUsers')   },
              { label: t('admin.fieldMaxParticipants'),    key: 'maxPax', min: 3,  max: 500, suffix: t('admin.suffixUsers')   },
            ] as const).map(f => (
              <div key={f.key} className="rule-item">
                <label>{f.label}</label>
                <div className="input-group">
                  <input type="number" className="input-small" disabled={!rn001Enabled}
                    value={rn001[f.key]} min={f.min} max={f.max}
                    onChange={e => setRn001(s => ({ ...s, [f.key]: Number(e.target.value) }))} />
                  <span className="input-suffix">{f.suffix}</span>
                </div>
              </div>
            ))}
            <div className="rule-customizable-bar">
              <button className={`rule-customizable-btn${rn001OU ? ' active' : ''}`} onClick={() => setRn001OU(v => !v)}>
                <i className="fas fa-sliders-h" /> {t('admin.customizableByOU')}
                <i className="fas fa-chevron-down rule-cust-chevron" />
              </button>
              <button className="rule-expand-ou-btn" title={t('common.expandModal')}
                onClick={() => { setRn001Modal(true); setRn001OU(false); }}>
                <i className="fas fa-expand-alt" />
              </button>
            </div>
          </div>
          {(rn001OU || rn001Modal) && (
            <RuleOUSection
              items={ouItems}
              isInModal={rn001Modal}
              onCloseModal={() => setRn001Modal(false)}
              modalTitle={t('admin.rn001Title')}
              fields={[
                { label: t('admin.fieldMinSessionDuration'), key: 'minDur', value: rn001.minDur, min: 5,  max: 60,  suffix: t('admin.suffixMinutes') },
                { label: t('admin.fieldMaxSessionDuration'), key: 'maxDur', value: rn001.maxDur, min: 30, max: 480, suffix: t('admin.suffixMinutes') },
                { label: t('admin.fieldMinParticipants'),    key: 'minPax', value: rn001.minPax, min: 2,  max: 50,  suffix: t('admin.suffixUsers')   },
                { label: t('admin.fieldMaxParticipants'),    key: 'maxPax', value: rn001.maxPax, min: 3,  max: 500, suffix: t('admin.suffixUsers')   },
              ]}
              initialOuOverrides={rulesConfig?.rn001OuOverrides}
              onSaveOu={(ouId, vals) => handleOuSave(RN001_FIELD_CODES, ouId, vals)}
            />
          )}
        </div>

        {/* RN-002: Reglas de Exclusión */}
        <div className={`rules-card${!rn002Enabled ? ' rules-card-disabled' : ''}`}>
          <div className="rules-card-header">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0 }}><i className="fas fa-ban" /> {t('admin.rn002Title')}</h3>
                <label className="switch" style={{ marginLeft: 'auto' }}>
                  <input type="checkbox" checked={rn002Enabled} onChange={e => setRn002Enabled(e.target.checked)} />
                  <span className="slider" />
                </label>
              </div>
              <span className="rules-subtitle">{t('admin.rn002Subtitle')}</span>
            </div>
          </div>
          <div className="rules-card-body">
            <div className="rule-item checkbox-rule">
              <label className="checkbox-container">
                <input type="checkbox" checked={exclRecur} disabled={!rn002Enabled}
                  onChange={e => setExclRecur(e.target.checked)} />
                <span className="checkmark" />
                <span className="rule-text">
                  <strong>{t('admin.fieldExclRecurring')}</strong>
                  <small>{t('admin.fieldExclRecurringSub')}</small>
                </span>
              </label>
              {exclRecur && (
                <div className="rule-keyword-sub">
                  <div className="rule-kw-label">
                    {t('admin.rn002KwLabel')}
                    <span className="rule-kw-hint">
                      {keywords.length === 0 ? ` ${t('admin.rn002KwHint')}` : ''}
                    </span>
                  </div>
                  {keywords.length > 0 && (
                    <div className="rule-kwchips">
                      {keywords.map(kw => (
                        <span key={kw} className="rule-kwchip">
                          {kw}
                          <button className="btn btn--secondary" onClick={() => removeKeyword(kw)} title={t('admin.kwRemove')}>
                            <i className="fas fa-times" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="rule-kw-adder">
                    <input
                      type="text"
                      className="rule-kw-input"
                      placeholder={t('admin.rn002KwPlaceholder')}
                      value={kwInput}
                      onChange={e => setKwInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addKeyword(); }}
                      disabled={!rn002Enabled}
                    />
                    <button className="rule-kw-add-btn" onClick={addKeyword} title={t('admin.kwAdd')} disabled={!rn002Enabled}>
                      <i className="fas fa-plus" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="rule-item checkbox-rule">
              <label className="checkbox-container">
                <input type="checkbox" checked={exclWork} disabled={!rn002Enabled}
                  onChange={e => setExclWork(e.target.checked)} />
                <span className="checkmark" />
                <span className="rule-text">
                  <strong>{t('admin.fieldExclWorkHours')}</strong>
                  <small>{t('admin.fieldExclWorkHoursSub')}</small>
                </span>
              </label>
            </div>
            <div className="rule-customizable-bar">
              <button className={`rule-customizable-btn${rn002OU ? ' active' : ''}`} onClick={() => setRn002OU(v => !v)}>
                <i className="fas fa-sliders-h" /> {t('admin.customizableByOU')}
                <i className="fas fa-chevron-down rule-cust-chevron" />
              </button>
              <button className="rule-expand-ou-btn" title={t('common.expandModal')}
                onClick={() => { setRn002Modal(true); setRn002OU(false); }}>
                <i className="fas fa-expand-alt" />
              </button>
            </div>
          </div>
          {(rn002OU || rn002Modal) && (
            <Rn002OUSection
              items={ouItems}
              labelRecur={t('admin.fieldExclRecurring')}
              labelWork={t('admin.fieldExclWorkHours')}
              globalRecurr={exclRecur}
              globalWork={exclWork}
              isInModal={rn002Modal}
              onCloseModal={() => setRn002Modal(false)}
              modalTitle={t('admin.rn002Title')}
              initialOuOverrides={rulesConfig?.rn002OuOverrides}
              onSaveOu={handleRn002OuSave}
            />
          )}
        </div>

        {/* RN-003: Horario Laboral por OU — full width */}
        <div className={`rules-card full-width${!rn003Enabled ? ' rules-card-disabled' : ''}`}>
          <div className="rules-card-header">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0 }}><i className="fas fa-clock" /> {t('admin.rn003Title')}</h3>
                <label className="switch" style={{ marginLeft: 'auto' }}>
                  <input type="checkbox" checked={rn003Enabled} onChange={e => setRn003Enabled(e.target.checked)} />
                  <span className="slider" />
                </label>
              </div>
              <span className="rules-subtitle">{t('admin.rn003Subtitle')}</span>
            </div>
          </div>
          <div className="rules-card-body">
            <div className="rule-item">
              <label>{t('admin.rn003GlobalDefault')}</label>
              <div className="time-inputs">
                <input type="time" value={rn003.from} disabled={!rn003Enabled}
                  onChange={e => setRn003(s => ({ ...s, from: e.target.value }))} />
                <span>{t('admin.rn003TimeConnector')}</span>
                <input type="time" value={rn003.to} disabled={!rn003Enabled}
                  onChange={e => setRn003(s => ({ ...s, to: e.target.value }))} />
              </div>
            </div>
            <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginTop: -8 }}>
              {t('admin.rn003GlobalNote')}
            </small>
            <div className="rule-customizable-bar">
              <button className={`rule-customizable-btn${rn003OU ? ' active' : ''}`} onClick={() => setRn003OU(v => !v)}>
                <i className="fas fa-sliders-h" /> {t('admin.customizableByOU')}
                <i className="fas fa-chevron-down rule-cust-chevron" />
              </button>
              <button className="rule-expand-ou-btn" title={t('common.expandModal')}
                onClick={() => { setRn003Modal(true); setRn003OU(false); }}>
                <i className="fas fa-expand-alt" />
              </button>
            </div>
          </div>
          {(rn003OU || rn003Modal) && (
            <RuleTimeOUSection
              items={ouItems}
              globalFrom={rn003.from}
              globalTo={rn003.to}
              isInModal={rn003Modal}
              onCloseModal={() => setRn003Modal(false)}
              modalTitle={t('admin.rn003Title')}
            />
          )}
        </div>

        {/* RN-004: Límites por Usuario */}
        <div className={`rules-card${!rn004Enabled ? ' rules-card-disabled' : ''}`}>
          <div className="rules-card-header">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0 }}><i className="fas fa-user-clock" /> {t('admin.rn004Title')}</h3>
                <label className="switch" style={{ marginLeft: 'auto' }}>
                  <input type="checkbox" checked={rn004Enabled} onChange={e => setRn004Enabled(e.target.checked)} />
                  <span className="slider" />
                </label>
              </div>
              <span className="rules-subtitle">{t('admin.rn004Subtitle')}</span>
            </div>
          </div>
          <div className="rules-card-body">
            {([
              { label: t('admin.fieldMaxSurveysWeek'),           key: 'maxWeek',  min: 1, max: 50,  suffix: t('admin.suffixSurveysWeek') },
              { label: t('admin.fieldMaxParticipantsPerSurvey'), key: 'maxPer',   min: 1, max: 15,  suffix: t('admin.suffixPersons')     },
              { label: t('admin.fieldMinInterval'),              key: 'interval', min: 1, max: 24,  suffix: t('admin.suffixHours')       },
            ] as const).map(f => (
              <div key={f.key} className="rule-item">
                <label>{f.label}</label>
                <div className="input-group">
                  <input type="number" className="input-small" disabled={!rn004Enabled}
                    value={rn004[f.key]} min={f.min} max={f.max}
                    onChange={e => setRn004(s => ({ ...s, [f.key]: Number(e.target.value) }))} />
                  <span className="input-suffix">{f.suffix}</span>
                </div>
              </div>
            ))}
            <div className="rule-customizable-bar">
              <button className={`rule-customizable-btn${rn004OU ? ' active' : ''}`} onClick={() => setRn004OU(v => !v)}>
                <i className="fas fa-sliders-h" /> {t('admin.customizableByOU')}
                <i className="fas fa-chevron-down rule-cust-chevron" />
              </button>
              <button className="rule-expand-ou-btn" title={t('common.expandModal')}
                onClick={() => { setRn004Modal(true); setRn004OU(false); }}>
                <i className="fas fa-expand-alt" />
              </button>
            </div>
          </div>
          {(rn004OU || rn004Modal) && (
            <RuleOUSection
              items={ouItems}
              isInModal={rn004Modal}
              onCloseModal={() => setRn004Modal(false)}
              modalTitle={t('admin.rn004Title')}
              fields={[
                { label: t('admin.fieldMaxSurveysWeek'),           key: 'maxWeek',  value: rn004.maxWeek,  min: 1, max: 50,  suffix: t('admin.suffixSurveysWeek') },
                { label: t('admin.fieldMaxParticipantsPerSurvey'), key: 'maxPer',   value: rn004.maxPer,   min: 1, max: 15,  suffix: t('admin.suffixPersons')     },
                { label: t('admin.fieldMinInterval'),              key: 'interval', value: rn004.interval, min: 1, max: 24,  suffix: t('admin.suffixHours')       },
              ]}
              initialOuOverrides={rulesConfig?.rn004OuOverrides}
              onSaveOu={(ouId, vals) => handleOuSave(RN004_FIELD_CODES, ouId, vals)}
            />
          )}
        </div>

        {/* RN-005: Límite Diario de Encuestas */}
        <div className={`rules-card${!rn005Enabled ? ' rules-card-disabled' : ''}`}>
          <div className="rules-card-header">
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h3 style={{ margin: 0 }}><i className="fas fa-clock" /> {t('admin.rn005Title')}</h3>
                <label className="switch" style={{ marginLeft: 'auto' }}>
                  <input type="checkbox" checked={rn005Enabled} onChange={e => setRn005Enabled(e.target.checked)} />
                  <span className="slider" />
                </label>
              </div>
              <span className="rules-subtitle">{t('admin.rn005Subtitle')}</span>
            </div>
          </div>
          <div className="rules-card-body">
            <div className="rule-item">
              <label>{t('admin.fieldMaxSurveysDay')}</label>
              <div className="input-group">
                <input type="number" className="input-small" disabled={!rn005Enabled}
                  value={rn005.maxDay} min={1} max={10}
                  onChange={e => setRn005(s => ({ ...s, maxDay: Number(e.target.value) }))} />
                <span className="input-suffix">{t('admin.suffixSurveysDay')}</span>
              </div>
            </div>
            <div className="anonymity-description">
              <i className="fas fa-info-circle" />
              <p>{t('admin.rn005Info')}</p>
            </div>
            <div className="rule-customizable-bar">
              <button className={`rule-customizable-btn${rn005OU ? ' active' : ''}`} onClick={() => setRn005OU(v => !v)}>
                <i className="fas fa-sliders-h" /> {t('admin.customizableByOU')}
                <i className="fas fa-chevron-down rule-cust-chevron" />
              </button>
              <button className="rule-expand-ou-btn" title={t('common.expandModal')}
                onClick={() => { setRn005Modal(true); setRn005OU(false); }}>
                <i className="fas fa-expand-alt" />
              </button>
            </div>
          </div>
          {(rn005OU || rn005Modal) && (
            <RuleOUSection
              items={ouItems}
              isInModal={rn005Modal}
              onCloseModal={() => setRn005Modal(false)}
              modalTitle={t('admin.rn005Title')}
              fields={[
                { label: t('admin.fieldMaxSurveysDay'), key: 'maxDay', value: rn005.maxDay, min: 1, max: 10, suffix: t('admin.suffixSurveysDay') },
              ]}
              initialOuOverrides={rulesConfig?.rn005OuOverrides}
              onSaveOu={(ouId, vals) => handleOuSave(RN005_FIELD_CODES, ouId, vals)}
            />
          )}
        </div>

      </div>

      <div className="rules-footer">
        <button className="btn btn-secondary" onClick={resetToDefaults}>
          {t('admin.cancelChanges')}
        </button>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
        >
          {saveStatus === 'saving' && <i className="fas fa-spinner fa-spin" />}
          {saveStatus === 'saved'  && <i className="fas fa-check" />}
          {saveStatus === 'error'  && <i className="fas fa-exclamation-triangle" />}
          {saveStatus === 'idle'   && <i className="fas fa-save" />}
          {' '}{t('admin.saveConfig')}
        </button>
      </div>
    </div>
  );
}
